/** Applied to `#chat-message-{id}`; CSS flashes `[data-chat-scroll-anchor-bubble]` (see `index.css`). */
const CHAT_MESSAGE_REFERENCE_FLASH_CLASS = 'chat-message-reference-flash-target'

const FLASH_ANIMATION_MS = 550
/** If `scrollend` never fires (older engines / edge cases), flash soon after—keeps UX snappy. */
const FLASH_IF_NO_SCROLL_END_MS = 280

function findNearestVerticalScrollAncestor(element: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = element.parentElement
  while (node) {
    if (node === document.body || node === document.documentElement) break
    const { overflowY } = window.getComputedStyle(node)
    const scrollsVertically =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight + 1
    if (scrollsVertically) return node
    node = node.parentElement
  }
  if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
    return document.documentElement
  }
  return null
}

/**
 * Smooth-scrolls the anchor row into view, then runs a short background highlight on the bubble.
 * Listens for `scrollend` on the nearest scrollable ancestor (the chat list), not only on `document`,
 * so the highlight starts when the scroll animation finishes instead of waiting on a long fallback.
 */
export function scrollToChatMessageByIdAndFlash(messageId: string): void {
  const scrollAnchorRoot = document.getElementById(`chat-message-${messageId}`)
  if (!scrollAnchorRoot) return

  const scrollContainer = findNearestVerticalScrollAncestor(scrollAnchorRoot)

  let flashAlreadyStarted = false
  const runBackgroundFlashOnBubble = () => {
    if (flashAlreadyStarted) return
    flashAlreadyStarted = true
    scrollAnchorRoot.classList.add(CHAT_MESSAGE_REFERENCE_FLASH_CLASS)
    window.setTimeout(
      () => scrollAnchorRoot.classList.remove(CHAT_MESSAGE_REFERENCE_FLASH_CLASS),
      FLASH_ANIMATION_MS
    )
  }

  let scrollEndListenersStillAttached = true
  /** Browser timer id (`window.setTimeout`); avoid `ReturnType<typeof setTimeout>` which can be `NodeJS.Timeout` under mixed typings. */
  let fallbackFlashTimer: number

  const detachScrollEndListeners = (onScrollEnd: () => void) => {
    if (!scrollEndListenersStillAttached) return
    scrollEndListenersStillAttached = false
    scrollContainer?.removeEventListener('scrollend', onScrollEnd)
    document.removeEventListener('scrollend', onScrollEnd)
  }

  const onScrollEnd = () => {
    window.clearTimeout(fallbackFlashTimer)
    detachScrollEndListeners(onScrollEnd)
    runBackgroundFlashOnBubble()
  }

  scrollAnchorRoot.scrollIntoView({ behavior: 'smooth', block: 'center' })

  fallbackFlashTimer = window.setTimeout(() => {
    detachScrollEndListeners(onScrollEnd)
    runBackgroundFlashOnBubble()
  }, FLASH_IF_NO_SCROLL_END_MS)

  scrollContainer?.addEventListener('scrollend', onScrollEnd, { passive: true })
  document.addEventListener('scrollend', onScrollEnd, { passive: true })
}
